/* eslint-disable max-lines */
import moment from 'moment-timezone';
import _ from 'lodash';
import QueryString from 'qs';
import numeral from 'numeral';
import AppRouter from 'common/AppRouter';
import {
  ACCOUNT_TYPES_MAP,
  ORG_SIZE_CYCLE_TIME_METRICS_BENCHMARKS_VALUES,
  BENCHMARK_ORG_SIZE_KEYS
} from '@linearb/linjs-common';

import BrowserStorageAccessor from './BrowserStorageAccessor';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import {
  DAYS_PARAM,
  EMPTY_VALUE,
  ITERATION_SORTING_BY,
  LOCAL_STORAGE_KEYS,
  PROVIDERS_MAP,
  SEARCH_DATE_FORMAT,
  SENSOR_TYPES,
  SESSION_STORAGE_KEYS,
  TEAM_TYPES,
  TOO_LONG_ITERATION,
  SURVEY_PATH_PATTERN
} from 'common/constants/constants';
import { API, API_GATEWAY_URI } from 'common/constants/api-urls';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import QS from 'query-string';
import store from 'state/store/configureStore';
import { boardsProviderKeysSelector } from 'state/selectors/boards';
import emailProviderDomains from 'common/constants/emailProviderDomains';
import { SEARCH_TIMEDATE_FORMAT } from '@linearb/linui-utils';
import { getBenchmarkByAggAndOrgSize } from 'common/services/benchmarks.service';

TimeAgo.addLocale(en);
const timeAgo = new TimeAgo('en-US');

export const dispatchify = (dispatch, callers) => {
  return Object.entries(callers).reduce(
    (acc, [caller, callerFn]) =>
      Object.assign(acc, {
        [caller]: (...args) => dispatch(callerFn(...args))
      }),
    {}
  );
};

export const recall = (callers, shouldStopRecall, frequency) =>
  Object.entries(callers).reduce(
    (acc, [caller, callerFn]) =>
      Object.assign(acc, {
        [caller]: (...args) => {
          const interval = setInterval(() => {
            if (shouldStopRecall(callerFn, args)) {
              return clearInterval(interval);
            }
            callerFn(...args);
          }, frequency * 1000);
          return callerFn(...args);
        }
      }),
    {}
  );

export function parseJwt(token) {
  const base64Url = token?.split('.')[1];
  if (!base64Url) {
    return null;
  }
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(window.atob(base64));
}

export function enumerateDaysBetweenDates(startDate, endDate) {
  const dates = [];

  const currDate = moment(startDate).startOf('day');
  const lastDate = moment(endDate).startOf('day');
  dates.push(currDate.clone().format(SEARCH_DATE_FORMAT));

  while (currDate.add(1, DAYS_PARAM).diff(lastDate) < 0) {
    dates.push(currDate.clone().format(SEARCH_DATE_FORMAT));
  }

  dates.push(lastDate.clone().format(SEARCH_DATE_FORMAT));

  return dates;
}

export const roundTo100Percents = (data = [], key) => {
  if (!key || typeof key !== 'string') return data;

  const sum = _.sum(data.map(item => item[key]));
  if (sum === 0 || !sum || isNaN(sum)) {
    return data;
  }
  if (sum !== 100) {
    const diff = Math.abs(100 - sum);
    const maxItemIndex = _.indexOf(
      data,
      _.maxBy(data, item => item[key])
    );
    data[maxItemIndex] = {
      ...data[maxItemIndex],
      [key]:
        sum > 100
          ? data[maxItemIndex][key] - diff
          : data[maxItemIndex][key] + diff
    };
  }

  return data;
};

const getCurrentIterationByDates = (iterations, from, to) =>
  iterations.find(
    ({ start_date, end_date }) => start_date === from && end_date === to
  );

const getCurrentIterationById = (iterations, id) =>
  iterations.find(({ id: iterationId }) => iterationId === id);

export const getCurrentIteration = (iterations, from, to, id) => {
  const iterationById = getCurrentIterationById(iterations, id);
  const iterationByDates = getCurrentIterationByDates(iterations, from, to);
  return iterationById || iterationByDates;
};

export const checkIfIterationIsTooLong = ({
  iteration = {},
  isPreventTooLongIterationsEnabled = false
}) => {
  if (!isPreventTooLongIterationsEnabled) {
    return false;
  }
  const startDate = new Date(iteration.start_date);
  const endDate = new Date(iteration.end_date);
  const differenceInTime = endDate.getTime() - startDate.getTime();
  const differenceInDays = differenceInTime / (1000 * 3600 * 24);
  return differenceInDays > TOO_LONG_ITERATION;
};

const BENCHMARKS_STATES = {
  ELITE: 'Elite',
  GOOD: 'Good',
  FAIR: 'Fair',
  POOR: 'Poor',
  N_A: 'N/A'
};

export const BENCHMARKS_COLORS = {
  ELITE: 'malachiteB',
  GOOD: 'cerulean',
  FAIR: 'neonCarrot',
  POOR: 'radicalRed',
  N_A: 'gray'
};

export const compareMetricToIndustryStandard = (
  metricName,
  value = null,
  metricsCalculationStrategy = 'avg',
  benchmarkOrgSize = BENCHMARK_ORG_SIZE_KEYS.all
) => {
  const defaultCallback = () => false;

  const benchmark = ORG_SIZE_CYCLE_TIME_METRICS_BENCHMARKS_VALUES[metricName];
  const metricBenchmarksIntervals = getBenchmarkByAggAndOrgSize({
    benchmark,
    metricsCalculationStrategy,
    benchmarkOrgSize,
    isCycletimeMetrics: true
  });

  if (value === null || !metricBenchmarksIntervals) {
    return { label: BENCHMARKS_STATES.N_A, color: BENCHMARKS_COLORS.N_A };
  }
  const {
    eliteCondition = defaultCallback,
    goodCondition = defaultCallback,
    poorCondition = defaultCallback,
    fairCondition = defaultCallback
  } = metricBenchmarksIntervals;
  if (eliteCondition(value))
    return { label: BENCHMARKS_STATES.ELITE, color: BENCHMARKS_COLORS.ELITE };
  if (goodCondition(value))
    return { label: BENCHMARKS_STATES.GOOD, color: BENCHMARKS_COLORS.GOOD };
  if (fairCondition(value))
    return { label: BENCHMARKS_STATES.FAIR, color: BENCHMARKS_COLORS.FAIR };
  if (poorCondition(value))
    return { label: BENCHMARKS_STATES.POOR, color: BENCHMARKS_COLORS.POOR };

  return { label: BENCHMARKS_STATES.N_A, color: BENCHMARKS_COLORS.N_A };
};

export const extractFromQueryString = (key, defaultValue, query) => {
  const search = QueryString.parse(query, { ignoreQueryPrefix: true });

  return _.get(search, key, defaultValue);
};

export const numeralify = (value, nonNumeralFn = value => value) => {
  const numVal = _.toNumber(value);
  return !isNaN(numVal) && (numVal < -1000 || numVal > 1000)
    ? numeral(numVal).format('0.0a')
    : nonNumeralFn(value);
};

export const isTeamLicense = () =>
  BrowserStorageAccessor().local.get(LOCAL_STORAGE_KEYS.LICENSE) === 'team';

export const isTeamByLicense = license => license.licenseType === 'team';

export const getMinDateForDatePicker = license =>
  isTeamByLicense(license)
    ? moment()
        .subtract(license.retentionDays, DAYS_PARAM)
        .format(SEARCH_DATE_FORMAT)
    : null;

export const getRetentionDaysByLicense = license =>
  isTeamByLicense(license) ? license.retentionDays : null;

export const getRandomIntInclusive = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1) + min); // The maximum is inclusive and the minimum is inclusive
};

export const isAlphanumericString = value => {
  const alphanumericRegEx = /^[0-9A-Za-z\s\-_]+$/;
  return alphanumericRegEx.test(value);
};

export const redirectToProvider = (
  providerName,
  { redirectPath, query = {}, type = 'grant' } = {}
) => {
  const grantUrl = 'api/v1/rest/auth/grant';
  const identityUrl = 'api/v1/rest/public/auth/identity';
  const url = type === 'grant' ? grantUrl : identityUrl;
  const qs = QueryString.stringify(
    { redirectPath, ...query },
    { addQueryPrefix: true }
  );
  window.location.href = `${API_GATEWAY_URI}/${url}/${providerName}${qs}`;
};

export function omitFromQs(
  qsParamName,
  search = QueryString.parse(window.location.search, {
    ignoreQueryPrefix: true
  })
) {
  const newSearchString = `${QueryString.stringify(
    _.omit(search, [qsParamName])
  )}`;
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}${newSearchString ? `?${newSearchString}` : ''}`
  );
}

export function omitFromQsAndSaveInLocalStorage(localStorageKey, qsParamName) {
  const search = QueryString.parse(window.location.search, {
    ignoreQueryPrefix: true
  });

  if (search[qsParamName]) {
    BrowserStorageAccessor().local.set(localStorageKey, search[qsParamName]);
    omitFromQs(qsParamName, search);
  }
}

export const getTimeAgoFormat = time =>
  timeAgo.format(new Date(time), 'default');

export function omitFromQsAndSaveInSessionStorage(
  sessionStorageKey,
  qsParamName
) {
  const search = QueryString.parse(window.location.search, {
    ignoreQueryPrefix: true
  });
  BrowserStorageAccessor().session.set(sessionStorageKey, search[qsParamName]);
  omitFromQs(qsParamName, search);
}

export function pluralize(count, noun, suffix = 's', showCount = true) {
  return `${showCount ? count + ' ' : ''}${noun}${count !== 1 ? suffix : ''}`;
}

export function roundAndPluralize(count, noun = 'day', suffix = 's') {
  count = count ? count : 0;
  return pluralize(_.round(count, 1), noun, suffix, true);
}

export const getTimezone = () => moment.tz(moment.tz.guess()).format('z');

export const formatDateToUTC = (date, displayFormat, dbFormat) =>
  moment(date, displayFormat).utc().format(dbFormat);

export const formatUTCToTimezone = (date, format) =>
  moment.utc(date, format).tz(moment.tz.guess()).format(format);

export const getLastActivityPhrase = (
  provider_id,
  last_activity_provider_id,
  last_activity,
  author_id_field = 'author_id',
  contributorsByIds = {}
) => {
  const getAuthorName = (lastActivity, author_id_field) => {
    return contributorsByIds[_.get(lastActivity, author_id_field)]
      ? _.get(contributorsByIds, `${lastActivity[author_id_field]}.name`, null)
      : lastActivity.author_name;
  };

  const authorName =
    !_.isNil(provider_id) &&
    !_.isNil(last_activity_provider_id) &&
    last_activity[author_id_field]
      ? String(provider_id) === String(last_activity_provider_id)
        ? 'You'
        : getAuthorName(last_activity, author_id_field)
      : null;

  switch (last_activity.type) {
    case 'release':
      return `${authorName ? `${authorName} released` : 'Released'} ${moment(
        last_activity.time
      ).fromNow()}`;
    case 'commit':
      return `${
        authorName ? `${authorName} pushed` : 'Pushed'
      } a commit ${moment(last_activity.time).fromNow()}`;
    case 'pr-initiated':
      return `Waiting for review for ${moment(last_activity.time).fromNow(
        true
      )}`;
    case 'pr-merged':
      return `${authorName ? `${authorName} merged` : 'Merged'} ${moment(
        last_activity.time
      ).fromNow()}`;
    case 'review-commented':
      return `${authorName ? `${authorName} commented` : 'Commented'} ${moment(
        last_activity.time
      ).fromNow()}`;
    case 'review-approved':
      return `${
        authorName ? `${authorName} approved` : 'Approved'
      } this pr ${moment(last_activity.time).fromNow()}`;
    case 'review-changes_requested':
      return `${
        authorName ? `${authorName} requested` : 'Requested'
      } changes ${moment(last_activity.time).fromNow()}`;
    case 'pr-closed':
      return `PR closed ${moment(last_activity.time).fromNow()}`;
  }
};

export const columnSummaryMapper = (
  {
    tableCellProps: { style = {}, ...tableCellProps } = {},
    summary,
    ...column
  },
  data,
  verticalAlign = 'bottom'
) => ({
  ...column,
  tableCellProps: {
    ...tableCellProps,
    style: {
      ...style,
      verticalAlign
    }
  },
  ColumnHeaderComponent: ({ children }) => (
    <Flex
      data-cy={`column-name-${
        typeof column.name === 'string'
          ? column.name.replace(/ /g, '-')
          : column.nameForDataCy.replace(/ /g, '-')
      }`}
      direction='column'
      justify='center'
      height='100%'
    >
      {summary ? (
        typeof summary === 'string' ? (
          <Text size='x-x-large' weight='bold'>
            {_.get(data, summary, EMPTY_VALUE)}
          </Text>
        ) : _.isFunction(summary) ? (
          summary()
        ) : (
          summary
        )
      ) : null}
      {children}
    </Flex>
  )
});

export const isInOnboardingPath = () =>
  window.location.pathname.includes('onboarding');

export const onSelectionFn = (setItems, items, key) => id => {
  const restItems = items.filter(i => String(i[key]) !== String(id));
  const item = items.find(i => String(i[key]) === String(id));

  setItems([
    ...restItems,
    {
      ...item,
      isSelected: !item.isSelected
    }
  ]);
};

const getTeamProjectsIds = (selectedTeamProject, sensorName) => {
  if (!sensorName) return [];
  return _.get(
    selectedTeamProject,
    `filter.${PROVIDERS_MAP[sensorName.toLowerCase()].entities_settings_key}`,
    []
  );
};

export const getTeamSelectedProjects = (
  selectedTeamProject,
  selectedProjectSetting,
  sensorName
) => {
  if (!sensorName) return [];
  const boardsProviderKeys = boardsProviderKeysSelector(store.getState());

  const teamProviderIds = getTeamProjectsIds(selectedTeamProject, sensorName);
  const selectedEntities =
    selectedProjectSetting[
      PROVIDERS_MAP[sensorName.toLocaleLowerCase()].entities_settings_key
    ] || [];

  return teamProviderIds.map(({ provider_id }) => {
    const entityName = _.get(boardsProviderKeys, `${provider_id}.name`);
    return {
      ...selectedEntities.find(
        item => String(item.provider_id) === String(provider_id)
      ),
      ...(entityName ? { name: entityName } : {})
    };
  });
};

export const nullSafeGet = (object, path, defaultValue) =>
  _.get(object, path) || defaultValue;

export const getManagementTool = ({ sensorAuthorizations = [] }) =>
  sensorAuthorizations.find(
    ({ sensor_type_id }) => sensor_type_id === SENSOR_TYPES.PM
  );

export const orderIterations = iterations =>
  _(iterations)
    .map(iteration => {
      const { end_date, completed_at, state } = iteration;
      const activeEndDate =
        state === 'active'
          ? moment
              .max([moment(end_date), moment().startOf('day')])
              .format(SEARCH_DATE_FORMAT)
          : end_date;
      return {
        ...iteration,
        planned_end_date: end_date,
        end_date: completed_at || activeEndDate
      };
    })
    .orderBy(ITERATION_SORTING_BY.ORDER, ITERATION_SORTING_BY.DIRECTION) // make sure active iterations are first
    .value();

export const debouncedToggleSnackBar = _.debounce(
  (text, subtext, isSuccess, toggleSnackBar) => {
    toggleSnackBar({
      message: (
        <Flex direction='column'>
          <Text size='large' color='white'>
            {text}
          </Text>
          <Text size='large' color='white'>
            {subtext}
          </Text>
        </Flex>
      ),
      variant: isSuccess ? 'success' : 'error'
    });
  },
  1000,
  {
    heading: true
  }
);

export const getNumOfSelectableRepos = ({ license }) => license?.reposLeft;

export const convertBoolStrToBool = boolStr => {
  if (!boolStr || typeof boolStr !== 'string') return false;
  return boolStr.toLowerCase() === 'true';
};

export const getOrganizationFromBaseUrl = baseUrl => {
  return baseUrl.split('/').slice(-1)[0];
};

export const isMulti = arr => {
  return _.size(arr) > 1;
};

export const calculatePercentage = (
  partialValue = 0,
  totalValue = 0,
  round = true,
  ceil = false
) => {
  const value =
    partialValue && totalValue > 0
      ? _.min([(partialValue / totalValue) * 100, 100])
      : 0;
  if (round) return ceil ? Math.ceil(value) : _.round(value);
  return value;
};

export const redirectToTeamGoals = (
  history,
  selectedTeamObj,
  teamDashboardId
) => {
  const filtersSnapshot = {
    globallySelectedTeams: [selectedTeamObj],
    selectedRepo: null,
    selectedContributor: null
  };
  BrowserStorageAccessor().session.set(
    SESSION_STORAGE_KEYS.METRICS_FILTERS,
    JSON.stringify(filtersSnapshot)
  );
  const pathname = `${AppRouter.TEAM_GOALS.path}/${selectedTeamObj.id}`;
  history.push({
    pathname
  });
};

export const camelCaseToTitleCase = camelCase => {
  /*
                          was taken from: https://stackoverflow.com/a/23871638/9329163
                          examples:
                            thisIsCamelCase --> This Is Camel Case
                            this IsCamelCase --> This Is Camel Case
                            thisIsCamelCase123 --> This Is Camel Case123
                        */

  if (camelCase === null || camelCase === '') {
    return camelCase;
  }

  camelCase = camelCase.trim();
  let newText = '';
  for (let i = 0; i < camelCase.length; i++) {
    if (
      /[A-Z]/.test(camelCase[i]) &&
      i !== 0 &&
      /[a-z]/.test(camelCase[i - 1])
    ) {
      newText += ' ';
    }
    if (i === 0 && /[a-z]/.test(camelCase[i])) {
      newText += camelCase[i].toUpperCase();
    } else {
      newText += camelCase[i];
    }
  }

  return newText;
};

export const capitalizeAll = lowerCase => {
  // 'hello world' -> 'Hello World'
  return lowerCase.replace(/\w\S*/g, txt => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const sortObjectKeysBySortingArray = (o, sortingArr) => {
  return Object.keys(o)
    .sort((a, b) => {
      return sortingArr.indexOf(a) - sortingArr.indexOf(b);
    })
    .reduce((r, k) => {
      return (r[k] = o[k]), r;
    }, {});
};

export const getSelectedTabFromUrl = (tabs, history) =>
  tabs.find(
    ({ path }) =>
      QS.parseUrl(history.location.pathname).url === QS.parseUrl(path).url
  ) || tabs.find(({ type }) => type === 'default');

export const parseResponseMsg = (
  e = {},
  defaultMsg = 'an error has occurred, please try again.'
) => {
  try {
    const description = _.get(e, 'response.data.description', '[{}]');
    return _.get(JSON.parse(description), '[0].msg', defaultMsg);
  } catch (e) {
    return defaultMsg;
  }
};

export const generateSAMLLoginUrl = (email, testConnectivity = false) => {
  const testConnectivityParam = testConnectivity
    ? `&${LOCAL_STORAGE_KEYS.TEST_CONNECTIVITY}=true`
    : '';
  return `${API.SAML_PATH}?email=${email}${testConnectivityParam}`;
};

export const capitalizeDropdownDisplayValue = options => {
  return options.map(option => {
    return {
      ...option,
      displayValue: _.capitalize(_.get(option, 'displayValue', ''))
    };
  });
};

export const buildHierarchyTree = ({
  parentKey = 'parent_id',
  idKey = 'id',
  data = []
}) => {
  const tree = [];
  const childOf = {};
  data.forEach(item => {
    const id = _.get(item, idKey);
    const parent_id = _.get(item, parentKey);
    childOf[id] = childOf[id] || [];
    item.children = childOf[id];
    parent_id
      ? (childOf[parent_id] = childOf[parent_id] || []).push(item)
      : tree.push(item);
  });

  const _traverse = (element, path = []) => {
    element.path = path.concat(element.parent_id ? [element.parent_id] : []);
    element.children.forEach(child => {
      _traverse(child, element.path);
    });
  };

  tree.forEach(child => {
    _traverse(child, []);
  });

  return tree;
};

// gets array of teams and returns the all teams group from it if exists
// eslint-disable-next-line
export const getAllTeamsGroup = teams => {
  return teams.find(({ type }) => type === TEAM_TYPES.ALL_TEAMS) || {};
};

// gets array of teams and returns the all contributors team from it if exists
// eslint-disable-next-line
export const getAllContributorsTeam = teams => {
  return teams.find(({ type }) => type === TEAM_TYPES.ALL_CONTRIBUTORS) || {};
};

// eslint-disable-next-line
export const getAllTeamsGroupId = teams => {
  return getAllTeamsGroup(teams)?.id || null;
};

// eslint-disable-next-line
export const getAllContributorsTeamId = teams => {
  return getAllContributorsTeam(teams)?.id || null;
};

// creates a tree of teams where All teams group has no children
// the tree is : All teams group, All teams children, All contributors team (if exists)
export const getTreeWhenAllTeamsGroup = tree => {
  const allTeamsGroup = getAllTeamsGroup(tree);
  const allContributorsTeam = getAllContributorsTeam(tree);
  const childrenOfAllTeamsGroup = allTeamsGroup?.children;
  const allTeamsGroupNoChildren = _.cloneDeep(allTeamsGroup);
  // this is so that all teams group won't have an arrow with the heirchy
  allTeamsGroupNoChildren.children = [];

  if (!_.isEmpty(allContributorsTeam)) {
    return [
      allTeamsGroupNoChildren,
      allContributorsTeam,
      ...childrenOfAllTeamsGroup
    ];
  }

  return [allTeamsGroupNoChildren, ...childrenOfAllTeamsGroup];
};

// return an array of the children of the first level of the tree
export const getTreeSecondLevel = tree => {
  const arrOfChildren = [];
  for (let i = 0; i < tree.length; i++) {
    arrOfChildren.push(...tree[i]?.children);
  }

  return arrOfChildren;
};

export const isViewerAccount = (myAccount, defaultVal = null) => {
  return _.get(myAccount, 'type_id', defaultVal) === ACCOUNT_TYPES_MAP.Viewer;
};

export const generateTotalGroupAndTeamMsg = teamsWithExtraData => {
  if (!Array.isArray(teamsWithExtraData) || teamsWithExtraData.length === 0)
    return '';
  const { numOfGroups, numOfTeams } = teamsWithExtraData.reduce(
    (total, team) => {
      team.isParent ? (total.numOfGroups += 1) : (total.numOfTeams += 1);
      return total;
    },
    {
      numOfGroups: 0,
      numOfTeams: 0
    }
  );
  const conjunctionWord = numOfGroups && numOfTeams ? ' and ' : '';
  return `${
    numOfGroups ? pluralize(numOfGroups, 'group') + conjunctionWord : ''
  }${numOfTeams ? pluralize(numOfTeams, 'team') : ''}`;
};

export const isPublicEmail = email =>
  email
    ? emailProviderDomains.some(emailSuffix => email.endsWith(emailSuffix))
    : false;

export const getRemValueInPixel = () => (window.innerWidth > 1920 ? 13 : 11);

/**
 * Navigates to the `Book a Demo` external page
 * @returns {void}
 */
export function navigateBookDemoClick() {
  window.open('https://linearb.io/book-a-demo', '_blank');
}

export const downloadLink = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const omitDeepKeys = (obj, paths) => {
  const clonedObj = _.cloneDeep(obj);

  paths.forEach(path => {
    _.unset(clonedObj, path);
  });

  return clonedObj;
};

export const sleepDelay = seconds => {
  return new Promise(resolve => {
    setTimeout(resolve, seconds * 1000);
  });
};

export const roundValue = (value, decimal = 1) => {
  if (value === undefined) return 'N/A';
  if (value === null) return '';
  if (value === 0) return 0;

  const threshold = 1 / Math.pow(10, decimal); // e.g. 0.1 for decimal=1

  if (Math.abs(value) < threshold) {
    return `<${threshold}`;
  }

  return _.round(value, decimal);
};

export const isGroup = team => {
  return (
    team?.isParent ||
    team?.type === TEAM_TYPES.GROUP ||
    team?.children?.length > 0
  );
};

export const isTeam = team => {
  return (
    !!team &&
    team !== 'all-teams' &&
    !isGroup(team) &&
    team?.type !== TEAM_TYPES.ALL_CONTRIBUTORS &&
    team?.type !== TEAM_TYPES.ALL_TEAMS
  );
};

// orderes a tree, the order: groups first and then teams, in alfabetical order.
export const traverseAndOrder = (obj, path) => {
  const { id, name } = obj;
  const childOptions = obj['children'].map(child =>
    traverseAndOrder(child, path.concat([{ name, id }]))
  );
  const currentObj = _.cloneDeep(obj);

  currentObj['children'] = _.orderBy(childOptions, [
    item => (isGroup(item) ? 1 : 2),
    item => item?.name?.toLowerCase()
  ]);

  currentObj.path = path;

  return currentObj;
};

// a function that gets a before and after
// and returns before_timestamp and after_timestamp
export const handleTimeFilterAsTimestamps = (filterPayload, filterKey) => {
  const transformedObject = {};
  if (!filterPayload?.[filterKey]) {
    return transformedObject;
  }

  const filterValues = filterPayload[filterKey];

  if (filterValues.before) {
    // we need the last second of the day
    transformedObject.before_timestamp = moment(filterValues.before)
      .add(1, 'days')
      .subtract(1, 'second')
      .utc()
      .format(SEARCH_TIMEDATE_FORMAT);
  }
  if (filterValues.after) {
    transformedObject.after_timestamp = moment(filterValues.after)
      .utc()
      .format(SEARCH_TIMEDATE_FORMAT);
  }

  return { [filterKey]: transformedObject };
};

const lowercaseKeysDeep = _.memoize(obj => {
  if (_.isObject(obj)) {
    return _.mapKeys(_.mapValues(obj, lowercaseKeysDeep), (value, key) =>
      key.toLowerCase()
    );
  }
  return obj;
});

export const getCaseInsensitive = (obj, path = '', defaultValue) => {
  try {
    const normalizedObj = lowercaseKeysDeep(obj);
    const normalizedPath = path.toLocaleLowerCase();
    return _.get(normalizedObj, normalizedPath) || defaultValue;
  } catch (e) {
    return defaultValue;
  }
};

export const getObjKeyByValue = (obj, value) =>
  Object.keys(obj).find(key => obj[key] === value);

export const urlStringHandler = (initialUrl = '') => {
  const [path, query = ''] = initialUrl.split('?');
  const parsed = QS.parse(query);

  let currentQuery = { ...parsed };

  const api = {
    deleteQuery(key) {
      const { [key]: _, ...rest } = currentQuery;
      currentQuery = rest;
      return api;
    },
    addQuery(key, value) {
      currentQuery = { ...currentQuery, [key]: value };
      return api;
    },
    toString() {
      const queryStr = QS.stringify(currentQuery);
      return queryStr ? `${path}?${queryStr}` : path;
    },
    get value() {
      return api.toString();
    }
  };

  return api;
};

/**
 * Checks if the current flow is a survey flow, based on session storage.
 * @returns {boolean} True if the redirect path includes 'surveys/execute', otherwise false.
 */
export const isSurveyFlow = () => {
  const sessionStorageAccessor = BrowserStorageAccessor().session;
  const redirectTo = sessionStorageAccessor.get(
    SESSION_STORAGE_KEYS.REDIRECT_TO
  );
  return (
    redirectTo?.includes(SURVEY_PATH_PATTERN) ||
    window.location.pathname.includes(SURVEY_PATH_PATTERN)
  );
};

export const getDefaultTimeRange = () => {
  return {
    before: moment().format(SEARCH_DATE_FORMAT),
    after: moment().subtract(12, 'months').format(SEARCH_DATE_FORMAT)
  };
};
